import { useState, useEffect } from 'react';

/**
 * useLocalStorage Hook
 *
 * Syncs state with localStorage for auto-save functionality.
 * Automatically saves changes with debouncing.
 *
 * @param {string} key - localStorage key
 * @param {*} initialValue - default value if nothing in storage
 * @param {number} debounceMs - milliseconds to debounce saves (default: 2000)
 * @returns {[value, setValue, clearValue]} - state value, setter, and clear function
 */
export function useLocalStorage(key, initialValue, debounceMs = 2000) {
  // Get initial value from localStorage or use provided initial value
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Debounced save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        window.localStorage.setItem(key, JSON.stringify(storedValue));
      } catch (error) {
        console.error(`Error saving to localStorage key "${key}":`, error);

        // If quota exceeded, try to clear old drafts
        if (error.name === 'QuotaExceededError') {
          console.warn('localStorage quota exceeded. Attempting to clear old drafts...');
          clearOldDrafts();

          // Retry save after clearing
          try {
            window.localStorage.setItem(key, JSON.stringify(storedValue));
          } catch (retryError) {
            console.error('Failed to save even after clearing old drafts:', retryError);
          }
        }
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [key, storedValue, debounceMs]);

  // Clear function to remove item from localStorage
  const clearValue = () => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialValue);
    } catch (error) {
      console.error(`Error clearing localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setStoredValue, clearValue];
}

/**
 * Clear old WYSIWYG drafts (older than 7 days)
 */
function clearOldDrafts() {
  const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      // Only check wysiwyg draft keys
      if (key && key.startsWith('wysiwyg-draft-')) {
        try {
          const data = JSON.parse(localStorage.getItem(key));

          // Remove if older than 7 days
          if (data && data.timestamp && data.timestamp < sevenDaysAgo) {
            localStorage.removeItem(key);
          }
        } catch {
          // If we can't parse it, remove it anyway
          localStorage.removeItem(key);
        }
      }
    }
  } catch (error) {
    console.error('Error clearing old drafts:', error);
  }
}

export default useLocalStorage;
